import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

/* ---- Animated Counter ---- */
const Counter = ({ target, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const duration = 2000;
        const step = (timestamp) => {
          if (!start) start = timestamp;
          const progress = Math.min((timestamp - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
          else setCount(target);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

/* ---- Service Card ---- */
const ServiceCard = ({ icon, title, desc, delay }) => (
  <div className="home-service-card" style={{ animationDelay: `${delay}ms` }}>
    <div className="home-service-card__icon">{icon}</div>
    <h3>{title}</h3>
    <p>{desc}</p>
    <div className="home-service-card__line" />
    <Link to="/services" className="home-service-card__link">
      Learn More
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    </Link>
  </div>
);

/* ---- Reticle SVG ---- */
const Reticle = () => (
  <svg className="hero__reticle" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="reticleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FF4500" stopOpacity="0.9"/>
        <stop offset="100%" stopColor="#1565C0" stopOpacity="0.9"/>
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
        <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    {/* Outer ring */}
    <circle cx="150" cy="150" r="130" fill="none" stroke="url(#reticleGrad)" strokeWidth="1.5" strokeDasharray="20 8" filter="url(#glow)" opacity="0.6"/>
    {/* Middle ring */}
    <circle cx="150" cy="150" r="90" fill="none" stroke="url(#reticleGrad)" strokeWidth="2" filter="url(#glow)" opacity="0.7"/>
    {/* Inner ring */}
    <circle cx="150" cy="150" r="55" fill="none" stroke="#FF4500" strokeWidth="2.5" filter="url(#glow)"/>
    {/* Crosshairs */}
    <line x1="150" y1="10" x2="150" y2="90" stroke="url(#reticleGrad)" strokeWidth="2" strokeLinecap="round" filter="url(#glow)"/>
    <line x1="150" y1="210" x2="150" y2="290" stroke="url(#reticleGrad)" strokeWidth="2" strokeLinecap="round" filter="url(#glow)"/>
    <line x1="10" y1="150" x2="90" y2="150" stroke="url(#reticleGrad)" strokeWidth="2" strokeLinecap="round" filter="url(#glow)"/>
    <line x1="210" y1="150" x2="290" y2="150" stroke="url(#reticleGrad)" strokeWidth="2" strokeLinecap="round" filter="url(#glow)"/>
    {/* Tick marks */}
    <line x1="150" y1="95" x2="150" y2="105" stroke="#3B9EFF" strokeWidth="2"/>
    <line x1="150" y1="195" x2="150" y2="205" stroke="#3B9EFF" strokeWidth="2"/>
    <line x1="95" y1="150" x2="105" y2="150" stroke="#3B9EFF" strokeWidth="2"/>
    <line x1="195" y1="150" x2="205" y2="150" stroke="#3B9EFF" strokeWidth="2"/>
    {/* Center dot */}
    <circle cx="150" cy="150" r="8" fill="#FF4500" filter="url(#glow)"/>
    <circle cx="150" cy="150" r="4" fill="#fff"/>
    {/* Corner brackets */}
    <path d="M50 30 L30 30 L30 50" fill="none" stroke="#3B9EFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M250 30 L270 30 L270 50" fill="none" stroke="#3B9EFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M50 270 L30 270 L30 250" fill="none" stroke="#3B9EFF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M250 270 L270 270 L270 250" fill="none" stroke="#3B9EFF" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default function Home() {
  return (
    <main className="home">

      {/* ========== HERO ========== */}
      <section className="hero">
        <div className="hero__grid-bg" />
        <div className="hero__glow-red" />
        <div className="hero__glow-blue" />

        <div className="hero__content container">
          <div className="hero__text">
            <div className="hero__eyebrow">
              <span className="hero__blip" />
              Next-Generation Digital Solutions
            </div>
            <h1 className="hero__title">
              <span className="hero__title-lg">Build.</span>
              <span className="hero__title-lg hero__title-blue">Scale.</span>
              <span className="hero__title-lg">Dominate.</span>
            </h1>
            <p className="hero__slogan"><em>Target your success with accuracy</em></p>
            <p className="hero__desc">
              We craft precision-engineered web applications, systems, and mobile solutions
              that hit the mark — every time. Powered by cutting-edge technology and
              driven by results.
            </p>
            <div className="hero__actions">
              <Link to="/contact" className="btn btn--fire">
                Start Your Project
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/portfolio" className="btn btn--ghost">
                View Our Work
              </Link>
            </div>
            <div className="hero__trust">
              <span className="hero__trust-item">✦ Fast Delivery</span>
              <span className="hero__trust-item">✦ 24/7 Support</span>
              <span className="hero__trust-item">✦ Guaranteed Results</span>
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__reticle-wrap">
              <Reticle />
            </div>
            <div className="hero__badge hero__badge--top">
              <div className="hero__badge-dot" />
              <span>System Online</span>
            </div>
            <div className="hero__badge hero__badge--bottom">
              <span>Target Acquired</span>
              <div className="hero__badge-dot hero__badge-dot--blue" />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hero__scroll">
          <div className="hero__scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ========== STATS ========== */}
      <section className="stats">
        <div className="container stats__grid">
          {[
            { value: 50, suffix: '+', label: 'Projects Delivered' },
            { value: 30, suffix: '+', label: 'Happy Clients' },
            { value: 3, suffix: '+', label: 'Years Experience' },
            { value: 100, suffix: '%', label: 'Client Satisfaction' },
          ].map((stat, i) => (
            <div className="stat-item" key={i}>
              <div className="stat-item__value">
                <Counter target={stat.value} suffix={stat.suffix} />
              </div>
              <div className="stat-item__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ========== SERVICES PREVIEW ========== */}
      <section className="home-services">
        <div className="container">
          <div className="section-header">
            <div className="section-header__tag">What We Do</div>
            <h2 className="section-header__title">Precision-Crafted <span className="text-fire">Services</span></h2>
            <p className="section-header__sub">
              From concept to deployment, we deliver digital solutions built for performance,
              scalability, and impact.
            </p>
          </div>
          <div className="home-services__grid">
            <ServiceCard
              delay={0}
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                </svg>
              }
              title="Web Development"
              desc="Blazing-fast, responsive websites and web applications built with modern frameworks. React, Next.js, Vue — we deliver experiences that convert."
            />
            <ServiceCard
              delay={100}
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/>
                  <path d="M6 6h.01M6 18h.01"/>
                </svg>
              }
              title="Systems Development"
              desc="Robust backend systems, ERPs, CRMs, and enterprise solutions architected to scale with your business — secure, fast, and reliable."
            />
            <ServiceCard
              delay={200}
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
                </svg>
              }
              title="App Development"
              desc="Native and cross-platform mobile apps for iOS and Android. Beautiful UX, powerful features, and seamless performance on every device."
            />
            <ServiceCard
              delay={300}
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              }
              title="Digital Strategy"
              desc="From MVP to market leader — we plan, architect, and execute digital strategies that position you ahead of the competition."
            />
          </div>
          <div className="home-services__cta">
            <Link to="/services" className="btn btn--ice">
              Explore All Services
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ========== WHY US ========== */}
      <section className="why-us">
        <div className="container why-us__inner">
          <div className="why-us__left">
            <div className="section-header__tag">Why LaserGuided</div>
            <h2 className="why-us__title">We Don't Miss.<br/><span className="text-fire">Ever.</span></h2>
            <p className="why-us__desc">
              Our name says it all. Like a laser guided system, we lock on to your goals
              and execute with surgical precision. No guesswork. No waste. Just results
              that hit exactly where they need to.
            </p>
            <div className="why-us__points">
              {[
                { title: 'Precision Engineering', desc: 'Every line of code crafted with intent and purpose.' },
                { title: 'Agile Delivery', desc: 'Fast iterations, transparent communication, on-time shipping.' },
                { title: 'Long-term Partnership', desc: 'We grow with you — support, maintenance, and evolution.' },
              ].map((p, i) => (
                <div className="why-us__point" key={i}>
                  <div className="why-us__point-num">0{i + 1}</div>
                  <div>
                    <strong>{p.title}</strong>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn btn--fire">Meet The Team</Link>
          </div>
          <div className="why-us__right">
            <div className="why-us__card">
              <div className="why-us__card-glow" />
              <div className="why-us__card-content">
                <div className="why-us__tech-grid">
                  {['React', 'Node.js', 'Laravel', 'Python', 'Flutter', 'PostgreSQL', 'AWS', 'Docker'].map(tech => (
                    <div className="why-us__tech-pill" key={tech}>{tech}</div>
                  ))}
                </div>
                <div className="why-us__card-label">Our Tech Stack</div>
                <div className="why-us__card-sub">Modern. Scalable. Battle-tested.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="cta-banner">
        <div className="cta-banner__glow" />
        <div className="container cta-banner__inner">
          <div>
            <h2 className="cta-banner__title">Ready to Hit Your Target?</h2>
            <p className="cta-banner__sub">
              Let's build something extraordinary together. Your vision, our precision.
            </p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn btn--white">
              Start a Project
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <Link to="/portfolio" className="btn btn--ghost-white">See Our Work</Link>
          </div>
        </div>
      </section>

    </main>
  );
}
